<template>
  <div class="flex flex-col gap-16">
    <!-- Top controls (Map filter placeholder) -->
    <div class="flex gap-4 justify-between">
      <div class="flex flex-col gap-4 flex-1">
        <h1 class="text-4xl font-medium text-text-primary">{{ $t('coach.page.title') }}</h1>
        <p class="text-text-secondary text-sm font-light w-2/3">
          {{ $t('coach.page.tagline') }}
        </p>
      </div>
      <div class="h-8 w-[240px] bg-gray-800 rounded-md animate-pulse" />
    </div>

    <!-- Overview Section -->
    <div class="flex flex-col gap-4 -mt-8">
      <h1 class="text-2xl font-medium text-text-primary">{{ $t('common.overview') }}</h1>
      <div class="gap-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        <!-- Highlights card -->
        <div class="flex flex-col bg-gray-900 rounded-lg overflow-hidden flex-1 divide-y divide-gray-950 col-span-1">
          <div class="flex flex-col gap-3 p-6 flex-1">
            <div class="h-5 w-32 bg-gray-800 rounded animate-pulse" />
            <div class="h-4 w-3/4 bg-gray-800 rounded animate-pulse" />
            <div class="h-4 w-1/2 bg-gray-800 rounded animate-pulse" />
            <div class="mt-auto h-9 w-full bg-gray-800 rounded-md animate-pulse" />
          </div>
          <div class="flex gap-2 px-6 py-4 items-center">
            <div class="h-4 w-24 bg-gray-800 rounded animate-pulse" />
            <div class="h-4 w-12 bg-gray-800 rounded animate-pulse" />
            <div class="h-5 w-16 bg-gray-800 rounded animate-pulse" />
          </div>
          <div class="flex gap-2 px-6 py-4 items-center">
            <div class="h-4 w-24 bg-gray-800 rounded animate-pulse" />
            <div class="h-4 w-12 bg-gray-800 rounded animate-pulse" />
            <div class="h-5 w-16 bg-gray-800 rounded animate-pulse" />
          </div>
        </div>
        <!-- Matches/Winrate/Recent Results card -->
        <div class="flex flex-col bg-gray-900 rounded-lg overflow-hidden flex-1 divide-y divide-gray-950 col-span-1">
          <div class="flex gap-2 p-6 flex-1">
            <div class="flex flex-col gap-2 flex-1">
              <div class="h-4 w-20 bg-gray-800 rounded animate-pulse" />
              <div class="h-6 w-10 bg-gray-800 rounded animate-pulse" />
            </div>
            <div class="flex flex-col gap-2 flex-1">
              <div class="h-4 w-16 bg-gray-800 rounded animate-pulse" />
              <div class="h-6 w-12 bg-gray-800 rounded animate-pulse" />
            </div>
            <div class="flex flex-col gap-2 flex-1">
              <div class="h-4 w-24 bg-gray-800 rounded animate-pulse" />
              <div class="h-6 w-20 bg-gray-800 rounded animate-pulse" />
            </div>
          </div>
          <div class="flex gap-2 px-6 py-4 items-center">
            <div class="h-4 w-24 bg-gray-800 rounded animate-pulse" />
            <div class="h-5 w-10 bg-gray-800 rounded animate-pulse" />
          </div>
          <div class="flex gap-2 px-6 py-4 items-center">
            <div class="h-4 w-20 bg-gray-800 rounded animate-pulse" />
            <div class="h-5 w-10 bg-gray-800 rounded animate-pulse" />
          </div>
          <div class="flex gap-2 px-6 py-4 items-center">
            <div class="h-4 w-12 bg-gray-800 rounded animate-pulse" />
            <div class="h-5 w-10 bg-gray-800 rounded animate-pulse" />
          </div>
        </div>
        <!-- Routine card -->
        <div class="flex flex-col bg-gray-900 rounded-lg overflow-hidden flex-1 divide-y divide-gray-950 col-span-1">
          <div class="flex flex-col gap-3 p-6 flex-1">
            <div class="h-5 w-28 bg-gray-800 rounded animate-pulse" />
            <div class="grid grid-cols-2 gap-3">
              <div v-for="i in 4" :key="i" class="h-9 w-full bg-gray-800 rounded-md animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Weaknesses Section -->
    <div class="flex flex-col gap-4">
      <div class="flex gap-2 items-center">
        <h1 class="text-text-primary text-2xl font-semibold flex">{{ $t('coach.shared.weaknesses') }}</h1>
        <span class="w-6 h-6 flex items-center justify-center text-danger-400 bg-danger-900 rounded-md" />
      </div>
      <div class="grid grid-cols-1 lg:grid-cols-3 w-full gap-6">
        <div v-for="i in 3" :key="i" class="bg-gray-900 rounded-lg p-6 h-[220px] animate-pulse" />
      </div>
    </div>

    <!-- Strengths Section -->
    <div class="flex flex-col gap-4">
      <div class="flex gap-2 items-center">
        <h1 class="text-text-primary text-2xl font-semibold flex">{{ $t('coach.shared.strengths') }}</h1>
        <span class="w-6 h-6 flex items-center justify-center text-success-400 bg-success-900 rounded-md" />
      </div>
      <div class="grid grid-cols-1 lg:grid-cols-3 w-full gap-6">
        <div v-for="i in 3" :key="i" class="bg-gray-900 rounded-lg p-6 h-[140px] animate-pulse" />
      </div>
    </div>

    <!-- Maps Section -->
    <div class="flex flex-col gap-4">
      <h1 class="text-text-primary text-2xl font-semibold flex items-center gap-2">{{ $t('common.maps') }}</h1>
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-y-4 lg:gap-6">
        <div class="flex flex-col gap-3 flex-1">
          <div class="flex flex-col flex-1">
            <div class="h-[220px] bg-gray-900 rounded-lg animate-pulse" />
          </div>
          <div class="flex flex-col">
            <div class="h-[220px] bg-gray-900 rounded-lg animate-pulse" />
          </div>
        </div>
        <div class="divide-y divide-gray-950 bg-gray-900 rounded-lg col-span-2 overflow-y-auto flex-1 max-h-[447px]">
          <div class="h-[447px] w-full bg-gray-800/60 animate-pulse" />
        </div>
      </div>
    </div>

    <!-- All Statistics Section -->
    <div class="flex flex-col gap-4">
      <div class="flex gap-4 items-center">
        <h1 class="text-text-primary text-2xl font-semibold flex flex-1">{{ $t('coach.shared.all_statistics') }}</h1>
        <div class="h-8 w-[192px] bg-gray-800 rounded-md animate-pulse" />
        <div class="h-8 w-[192px] bg-gray-800 rounded-md animate-pulse" />
        <div class="h-8 w-[192px] bg-gray-800 rounded-md animate-pulse" />
      </div>
      <div class="grid lg:grid-cols-2 w-full gap-6">
        <div class="bg-gray-900 rounded-lg p-6 h-[420px] animate-pulse" />
        <div class="bg-gray-900 rounded-lg p-6 h-[420px] animate-pulse" />
      </div>
    </div>

    <!-- Match History Section -->
    <div class="flex flex-col gap-4">
      <div class="flex w-full gap-4 items-center">
        <h1 class="text-text-primary font-semibold text-[24px] flex-1">{{ $t('coach.shared.match_history') }}</h1>
        <div class="h-8 w-[192px] bg-gray-800 rounded-md animate-pulse" />
        <div class="h-8 w-[192px] bg-gray-800 rounded-md animate-pulse" />
        <div class="h-8 w-[192px] bg-gray-800 rounded-md animate-pulse" />
      </div>
      <div class="w-full flex flex-col gap-[2px] rounded-lg overflow-hidden">
        <div class="bg-gray-900 text-text-primary text-xs tracking-[1.5px] flex-1 uppercase p-4" />
        <MatchSectionSkeleton />
      </div>
    </div>
  </div>
</template>

<script setup>
import MatchSectionSkeleton from '../Matches/lists/MatchSectionSkeleton.vue';
</script>
